import React from 'react';
import classNames from 'classnames';

export interface CompanyDataCardColumn {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  width?: string | number;
}

export interface CompanyDataCardRow {
  id?: string | number;
  values: Record<string, React.ReactNode>;
}

interface CompanyDataCardProps {
  columns: CompanyDataCardColumn[];
  rows: CompanyDataCardRow[];
  className?: string;
  /**
   * Optional renderer to customize how each cell is rendered.
   * If not provided, the value is rendered as plain text/ReactNode.
   */
  renderCell?: (
    columnKey: string,
    value: React.ReactNode,
    row: CompanyDataCardRow,
    rowIndex: number,
  ) => React.ReactNode;
}

const CompanyDataCard: React.FC<CompanyDataCardProps> = ({
  columns,
  rows,
  className,
  renderCell,
}) => {
  if (!columns || columns.length === 0 || !rows || rows.length === 0) {
    return null;
  }

  const gridTemplateColumns = columns
    .map((column) => {
      if (typeof column.width === 'number') {
        return `${column.width}px`;
      }
      if (typeof column.width === 'string' && column.width.trim().length > 0) {
        return column.width;
      }
      return '1fr';
    })
    .join(' ');

  return (
    <div className={classNames('data-card', className)}>
      <div className="data-card__header">
        {columns.map((column) => (
          <div
            key={column.key}
            className="data-card__header-cell"
            style={{
              textAlign: column.align || 'left',
              width: column.width,
            }}
          >
            <span>{column.label}</span>
          </div>
        ))}
      </div>
      <div className="data-card__content">
        {rows.map((row, rowIndex) => (
          <div key={row.id ?? rowIndex} className="data-card__row">
            {columns.map((column) => {
              const rawValue =
                row.values[column.key] !== undefined &&
                row.values[column.key] !== null
                  ? row.values[column.key]
                  : '-';

              const content = renderCell
                ? renderCell(column.key, rawValue, row, rowIndex)
                : rawValue;

              return (
                <div
                  key={column.key}
                  className="data-card__cell"
                  style={{
                    textAlign: column.align || 'left',
                    width: column.width,
                  }}
                >
                  <span className="regular-2">{content}</span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CompanyDataCard;
